import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useEyeconxStore } from '../../../../../../store/eyeconxStore';
import bookingService from '../../../../../../services/booking.service';
import { confirmDelete, showError, showSuccess } from '../../../../../../utils/ecxSwal';
import { ClinicInfoPanel } from '../../info/clinicInfoParts';
import ClinicSettingsSectionHeader from '../../subparts/ClinicSettingsSectionHeader';
import ClinicSettingsEmpty from '../../subparts/ClinicSettingsEmpty';
import ClinicSettingsCancelButton from '../../subparts/ClinicSettingsCancelButton';
import ClinicSettingsFormFooter from '../../subparts/ClinicSettingsFormFooter';
import ClinicSettingsListSkeleton from '../../subparts/ClinicSettingsListSkeleton';

const timeSlotOptions = [
    { value: '15', label: '15 minutes' },
    { value: '20', label: '20 minutes' },
    { value: '30', label: '30 minutes' },
    { value: '45', label: '45 minutes' },
    { value: '60', label: '60 minutes' },
];

const ManageAppointmentType = ({ onBack }) => {
    const clinicId = useEyeconxStore((state) => state.user.clinicId);
    const [appointmentTypes, setAppointmentTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [addingNew, setAddingNew] = useState(false);

    const { register, handleSubmit, reset, formState: { errors }, setValue } = useForm();

    const getAppointmentTypes = async () => {
        setLoading(true);
        try {
            const res = await bookingService.getAllAppointmentTypes(clinicId);
            setAppointmentTypes(res.data || []);
        } catch (error) {
            console.log('Error fetching appointment types:', error);
            showError({
                title: 'Load Failed',
                text: 'Unable to load appointment types. Please try again.',
            });
        } finally {
            setLoading(false);
        }
    };

    const addAppointmentType = async (data) => {
        setIsSaving(true);
        try {
            const appointmentTypeData = {
                apptType: data.apptType,
                timeSlot: parseInt(data.timeSlot, 10),
                isActive: data.status,
                clinicId,
            };
            const res = await bookingService.addUpdateAppointmentType(clinicId, appointmentTypeData);
            setAppointmentTypes((prev) => [...prev, res.data]);
            setAddingNew(false);
            reset();
            await showSuccess({
                title: 'Type Added',
                text: 'The appointment type was added successfully.',
            });
        } catch (error) {
            console.log('Error adding appointment type:', error);
            showError({
                title: 'Add Failed',
                text: 'Unable to add the appointment type. Please try again.',
            });
        } finally {
            setIsSaving(false);
        }
    };

    const updateAppointmentType = async (apptTypeId, data) => {
        setIsSaving(true);
        try {
            const updatedAppointmentTypeData = {
                id: apptTypeId,
                apptType: data.apptType,
                timeSlot: parseInt(data.timeSlot, 10),
                isActive: data.status,
                clinicId,
            };
            const res = await bookingService.addUpdateAppointmentType(clinicId, updatedAppointmentTypeData);
            setAppointmentTypes((prev) =>
                prev.map((type) =>
                    type.id === apptTypeId
                        ? {
                            ...type,
                            ...res.data,
                            apptType: data.apptType,
                            timeSlot: parseInt(data.timeSlot, 10),
                            isActive: data.status,
                        }
                        : type
                )
            );
            setEditingId(null);
            reset();
            await showSuccess({
                title: 'Type Updated',
                text: 'The appointment type was updated successfully.',
            });
        } catch (error) {
            console.log('Error updating appointment type:', error);
            showError({
                title: 'Update Failed',
                text: 'Unable to update the appointment type. Please try again.',
            });
        } finally {
            setIsSaving(false);
        }
    };

    const deleteAppointmentType = async (type) => {
        const result = await confirmDelete({
            title: 'Delete Appointment Type?',
            itemName: type.apptType,
            description: 'This appointment type will be removed from web booking.',
            onConfirm: () => bookingService.deleteAppointmentTypeById(clinicId, type.id),
        });

        if (result.isConfirmed) {
            setAppointmentTypes((prev) => prev.filter((item) => item.id !== type.id));
        }
    };

    const handleEdit = (appointmentType) => {
        setEditingId(appointmentType.id);
        setAddingNew(false);
        setValue('apptType', appointmentType.apptType);
        setValue('timeSlot', appointmentType.timeSlot.toString());
        setValue('status', appointmentType.isActive);
    };

    const handleCancel = () => {
        setEditingId(null);
        setAddingNew(false);
        reset();
    };

    const handleAddNew = () => {
        setAddingNew(true);
        setEditingId(null);
        reset({ apptType: '', timeSlot: '30', status: true });
    };

    useEffect(() => {
        getAppointmentTypes();
    }, [clinicId]);

    const renderTypeForm = (onSubmit, submitLabel) => (
        <form onSubmit={handleSubmit(onSubmit)} className="clinic-settings-record-form">
            <div className="clinic-settings-record-form__row">
                <div>
                    <label className="form-label small fw-semibold text-muted">Appointment Type</label>
                    <input
                        type="text"
                        className={`form-control form-control-sm ${errors.apptType ? 'is-invalid' : ''}`}
                        placeholder="Enter appointment type..."
                        {...register('apptType', {
                            required: 'Appointment type is required',
                            minLength: { value: 2, message: 'Appointment type must be at least 2 characters' },
                        })}
                    />
                    {errors.apptType && <div className="invalid-feedback">{errors.apptType.message}</div>}
                </div>
                <div>
                    <label className="form-label small fw-semibold text-muted">Duration</label>
                    <select
                        className={`form-select form-select-sm ${errors.timeSlot ? 'is-invalid' : ''}`}
                        {...register('timeSlot', {
                            required: 'Time slot is required',
                            validate: (value) => value > 0 || 'Time slot must be greater than 0',
                        })}
                    >
                        {timeSlotOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    {errors.timeSlot && <div className="invalid-feedback">{errors.timeSlot.message}</div>}
                </div>
            </div>
            <div className="d-flex align-items-center gap-2">
                <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" {...register('status')} defaultChecked />
                    <label className="form-check-label small">Active</label>
                </div>
            </div>
            <ClinicSettingsFormFooter isSaving={isSaving} submitLabel={submitLabel} />
        </form>
    );

    return (
        <>
            <ClinicSettingsSectionHeader
                title="Manage Appointment Types"
                icon="bi-calendar-check"
                subtitle="Manage appointment types and durations for web booking"
                actions={(
                    <div className="d-flex gap-2 flex-wrap">
                        {!loading && (
                            addingNew ? (
                                <ClinicSettingsCancelButton onClick={handleCancel} disabled={isSaving} />
                            ) : (
                                <button type="button" className="btn btn-sm btn-light-primary rounded-pill" onClick={handleAddNew}>
                                    <i className="bi bi-plus-circle me-1"></i>
                                    Add New Type
                                </button>
                            )
                        )}
                        <button type="button" className="btn btn-sm btn-outline-secondary rounded-pill" onClick={onBack}>
                            <i className="bi bi-arrow-left me-1"></i>
                            Back
                        </button>
                    </div>
                )}
            />

            {loading ? (
                <ClinicInfoPanel title="Appointment Types" icon="bi-calendar-event">
                    <ClinicSettingsListSkeleton rows={4} />
                </ClinicInfoPanel>
            ) : (
                <ClinicInfoPanel
                    title="Appointment Types"
                    icon="bi-calendar-event"
                    actions={editingId ? <ClinicSettingsCancelButton onClick={handleCancel} disabled={isSaving} /> : null}
                >
                    {addingNew && renderTypeForm(addAppointmentType, 'Add Type')}

                    {appointmentTypes.length === 0 && !addingNew ? (
                        <ClinicSettingsEmpty
                            icon="bi-calendar-x"
                            title="No appointment types found"
                            description="Get started by adding your first appointment type."
                            actionLabel="Add First Type"
                            onAction={handleAddNew}
                        />
                    ) : (
                        <div className="clinic-settings-record-list">
                            {appointmentTypes.map((type) => (
                                <div
                                    key={type.id}
                                    className={`clinic-settings-record-item ${editingId === type.id ? 'clinic-settings-record-item--editing' : ''}`}
                                >
                                    {editingId === type.id ? (
                                        <div className="flex-grow-1">
                                            {renderTypeForm((data) => updateAppointmentType(type.id, data), 'Save')}
                                        </div>
                                    ) : (
                                        <>
                                            <span className="clinic-settings-record-item__avatar">
                                                <i className="bi bi-calendar-event"></i>
                                            </span>
                                            <div className="clinic-settings-record-item__main">
                                                <h6 className="clinic-settings-record-item__title">{type.apptType}</h6>
                                                <p className="clinic-settings-record-item__meta mb-0">
                                                    <i className="bi bi-stopwatch me-1"></i>
                                                    {type.timeSlot} minutes · Modified {type.modifiedAt || '—'}
                                                </p>
                                            </div>
                                            <span className={`clinic-settings-status-pill ${type.isActive ? 'is-open' : 'is-closed'}`}>
                                                {type.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                            <div className="clinic-settings-record-item__actions">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary rounded-circle"
                                                    onClick={() => handleEdit(type)}
                                                    title="Edit"
                                                >
                                                    <i className="bi bi-pencil"></i>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger rounded-circle"
                                                    onClick={() => deleteAppointmentType(type)}
                                                    title="Delete"
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </ClinicInfoPanel>
            )}
        </>
    );
};

export default ManageAppointmentType;
